import React, { useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Plus, GripVertical, MoreHorizontal, Pencil, Trash2, Palette } from 'lucide-react';
import { TaskBoardTask, TaskProject, ProjectStickyNote } from '../../types';
import ProjectTaskCard from './ProjectTaskCard';
import ProjectStickyNotesColumn from './ProjectStickyNotesColumn';
import ProjectColumnFooter from './ProjectColumnFooter';
import { groupTasksByColumn, getProjectColumns, getColumnBgStyle, getColumnAggregations } from '../../utils/projectUtils';
import { COLUMN_BG_PRESETS, ColumnAggregationMode } from '../../utils/projectConstants';

interface ProjectBoardViewProps {
  project: TaskProject;
  tasks: TaskBoardTask[];
  onTaskClick: (task: TaskBoardTask) => void;
  onCreateTask: (title: string, status: string) => Promise<void>;
  onMoveTask: (taskId: string, status: string, position: number) => Promise<void>;
  onReorderColumns: (columns: string[]) => Promise<void>;
  onRenameColumn: (oldName: string, newName: string) => Promise<void>;
  onDeleteColumn: (columnName: string) => Promise<void>;
  onAddColumn: (name: string) => Promise<void>;
  onUpdateColumnColor: (columnName: string, color: string | null) => Promise<void>;
  onUpdateColumnAggregation: (columnName: string, mode: ColumnAggregationMode) => Promise<void>;
  onUpdateStickyNotes: (notes: ProjectStickyNote[]) => Promise<void>;
}

function computePosition(
  destCol: string,
  destIndex: number,
  taskId: string,
  grouped: Record<string, TaskBoardTask[]>
): number {
  const colTasks = (grouped[destCol] || []).filter(t => t.id !== taskId);
  const prev = colTasks[destIndex - 1];
  const next = colTasks[destIndex];
  if (prev && next) return Math.round(((prev.position ?? 0) + (next.position ?? 0)) / 2);
  if (prev) return (prev.position ?? 0) + 1000;
  if (next) return (next.position ?? 0) - 1000;
  return (destIndex + 1) * 1000;
}

const ProjectBoardView: React.FC<ProjectBoardViewProps> = ({
  project,
  tasks,
  onTaskClick,
  onCreateTask,
  onMoveTask,
  onReorderColumns,
  onRenameColumn,
  onDeleteColumn,
  onAddColumn,
  onUpdateColumnColor,
  onUpdateColumnAggregation,
  onUpdateStickyNotes,
}) => {
  const columns = getProjectColumns(project);
  const aggregations = getColumnAggregations(project);
  const grouped = groupTasksByColumn(tasks, columns);
  const wasDraggingRef = useRef(false);

  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [menuColumn, setMenuColumn] = useState<string | null>(null);
  const [renamingColumn, setRenamingColumn] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);

  const handleDragStart = () => {
    wasDraggingRef.current = true;
  };

  const handleDragEnd = async (result: DropResult) => {
    setTimeout(() => { wasDraggingRef.current = false; }, 100);

    const { destination, source, draggableId, type } = result;
    if (!destination) return;

    if (type === 'COLUMN') {
      const reordered = Array.from(columns);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);
      await onReorderColumns(reordered);
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const destCol = destination.droppableId;
    const position = computePosition(destCol, destination.index, draggableId, grouped);
    await onMoveTask(draggableId, destCol, position);
  };

  const submitNewTask = async (status: string) => {
    const title = newTitle.trim();
    if (!title) return;
    await onCreateTask(title, status);
    setNewTitle('');
    setAddingTo(null);
  };

  const submitNewColumn = async () => {
    const name = newColumnName.trim();
    if (!name) return;
    await onAddColumn(name);
    setNewColumnName('');
    setAddingColumn(false);
  };

  const submitRename = async () => {
    if (!renamingColumn) return;
    const name = renameValue.trim();
    if (!name || name === renamingColumn) {
      setRenamingColumn(null);
      return;
    }
    await onRenameColumn(renamingColumn, name);
    setRenamingColumn(null);
  };

  const defaultColClass = (isDraggingOver: boolean) =>
    isDraggingOver
      ? 'bg-indigo-50/80 dark:bg-indigo-950/20 ring-2 ring-indigo-300/50 dark:ring-indigo-700/50'
      : 'bg-gray-50/60 dark:bg-gray-900/40';

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden flex-col">
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Droppable droppableId="board-columns" direction="horizontal" type="COLUMN">
          {colProvided => (
            <div
              ref={colProvided.innerRef}
              {...colProvided.droppableProps}
              className="flex flex-1 min-h-0 gap-3 items-stretch overflow-x-auto overflow-y-hidden scrollbar-hide"
            >
            {columns.map((column, colIndex) => {
              const colTasks = grouped[column] || [];

              return (
                <Draggable key={column} draggableId={`col-${column}`} index={colIndex}>
                  {colDrag => (
                    <div
                      ref={colDrag.innerRef}
                      {...colDrag.draggableProps}
                      className="flex-shrink-0 w-[272px] flex flex-col min-h-0"
                    >
                      <div className="flex items-center gap-1 mb-2 px-1 group/col">
                        <span {...colDrag.dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 dark:text-gray-600">
                          <GripVertical className="w-3.5 h-3.5" />
                        </span>
                        {renamingColumn === column ? (
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={submitRename}
                            onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenamingColumn(null); }}
                            className="flex-1 text-xs font-semibold bg-transparent border-b border-indigo-400 outline-none text-gray-900 dark:text-white"
                          />
                        ) : (
                          <h3 className="flex-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
                            {column}
                          </h3>
                        )}
                        <span className="text-[10px] text-gray-400 tabular-nums">{colTasks.length}</span>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setMenuColumn(menuColumn === column ? null : column);
                              setShowColorPicker(null);
                            }}
                            className="p-0.5 rounded opacity-0 group-hover/col:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400"
                          >
                            <MoreHorizontal className="w-3.5 h-3.5" />
                          </button>
                          {menuColumn === column && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => { setMenuColumn(null); setShowColorPicker(null); }} />
                              <div className="absolute right-0 top-6 z-20 w-44 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1 text-xs">
                                <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2" onClick={() => { setRenamingColumn(column); setRenameValue(column); setMenuColumn(null); }}>
                                  <Pencil className="w-3 h-3" /> Renombrar
                                </button>
                                <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2" onClick={() => setShowColorPicker(showColorPicker === column ? null : column)}>
                                  <Palette className="w-3 h-3" /> Color de fondo
                                </button>
                                {showColorPicker === column && (
                                  <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-800">
                                    <div className="flex flex-wrap gap-1.5">
                                      <button
                                        type="button"
                                        title="Sin color"
                                        onClick={() => { onUpdateColumnColor(column, null); setMenuColumn(null); setShowColorPicker(null); }}
                                        className="w-5 h-5 rounded border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400"
                                      />
                                      {COLUMN_BG_PRESETS.filter(Boolean).map(c => (
                                        <button
                                          key={c}
                                          type="button"
                                          onClick={() => { onUpdateColumnColor(column, c); setMenuColumn(null); setShowColorPicker(null); }}
                                          className="w-5 h-5 rounded border border-gray-200 dark:border-gray-600 hover:scale-110 transition-transform"
                                          style={{ backgroundColor: c }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {columns.length > 1 && (
                                  <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 flex items-center gap-2" onClick={() => { onDeleteColumn(column); setMenuColumn(null); }}>
                                    <Trash2 className="w-3 h-3" /> Eliminar
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <Droppable droppableId={column} type="TASK">
                        {(provided, snapshot) => {
                          const customBg = getColumnBgStyle(column, project, snapshot.isDraggingOver);
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              style={customBg}
                              className={`flex-1 min-h-0 overflow-y-auto scrollbar-hide rounded-xl p-1.5 pb-3 space-y-1.5 transition-colors ${
                                customBg ? '' : defaultColClass(snapshot.isDraggingOver)
                              }`}
                            >
                              {colTasks.map((task, index) => (
                                <Draggable key={task.id} draggableId={task.id} index={index}>
                                  {(dragProvided, dragSnapshot) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                    >
                                      <ProjectTaskCard
                                        task={task}
                                        onClick={() => {
                                          if (!wasDraggingRef.current) onTaskClick(task);
                                        }}
                                        isDragging={dragSnapshot.isDragging}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}

                              {addingTo === column ? (
                                <div className="p-1">
                                  <textarea
                                    autoFocus
                                    rows={2}
                                    value={newTitle}
                                    onChange={e => setNewTitle(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitNewTask(column); }
                                      if (e.key === 'Escape') setAddingTo(null);
                                    }}
                                    placeholder="Título de la tarea..."
                                    className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 resize-none focus:ring-1 focus:ring-indigo-400 outline-none"
                                  />
                                  <div className="flex gap-1 mt-1">
                                    <button type="button" onClick={() => submitNewTask(column)} className="text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700">Agregar</button>
                                    <button type="button" onClick={() => setAddingTo(null)} className="text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">Cancelar</button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => { setAddingTo(column); setNewTitle(''); }}
                                  className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-colors"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Agregar tarea
                                </button>
                              )}
                            </div>
                          );
                        }}
                      </Droppable>

                      <ProjectColumnFooter
                        mode={aggregations[column] || 'none'}
                        tasks={colTasks}
                        onChangeMode={mode => onUpdateColumnAggregation(column, mode)}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
            {colProvided.placeholder}

            <div className="flex-shrink-0 flex flex-col items-start gap-2 self-stretch">
              {addingColumn ? (
                <div className="w-44 p-2 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-dashed border-gray-300 dark:border-gray-700">
                  <input
                    autoFocus
                    value={newColumnName}
                    onChange={e => setNewColumnName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitNewColumn(); if (e.key === 'Escape') setAddingColumn(false); }}
                    placeholder="Nombre..."
                    className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                  <div className="flex gap-1 mt-2">
                    <button type="button" onClick={submitNewColumn} className="text-xs px-2 py-1 rounded bg-indigo-600 text-white">Crear</button>
                    <button type="button" onClick={() => setAddingColumn(false)} className="text-xs px-2 py-1 text-gray-500">Cancelar</button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingColumn(true)}
                  title="Nueva columna"
                  className="w-9 h-9 flex items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/40 transition-colors mt-0.5"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}

              <ProjectStickyNotesColumn
                projectId={project.id}
                notes={project.sticky_notes ?? []}
                onUpdateNotes={onUpdateStickyNotes}
              />
            </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default ProjectBoardView;
